import { ReactNode } from 'react';

import { OfficeHoursMessage } from '@backrs_ui/grow-ui/src/models/models';

import { Box, SxProps, Theme } from '@mui/material';

import { SendMessageParams } from './ChatBox';
import { MultiNumericChatQuestion } from './MultiNumericChatQuestion';
import { MultiSelectChatQuestion } from './MultiSelectChatQuestion';
import { PromptButtons, PromptOption } from './PromptButtons';
import { SkipChatButton } from './SkipChatButton';
import { UserSuggestionChatQuestion } from './UserSuggestionChatQuestion';

type ComponentType = NonNullable<OfficeHoursMessage['componentType']>;

type StructuredQuestionInputProps = Readonly<{
  componentType: ComponentType;
  messageId: string;
  isTyping: boolean;
  onSelectOption: (option: string, additionalParams?: Partial<SendMessageParams>) => void;
}>;

/**
 * Renders the input component for structured-session question types streamed from the
 * agent (single_select with optional skip, multi_select, multi_numeric, numeric,
 * skip_only, user_suggestion). Returns null for any other component type so the caller
 * can fall through to its own branches (file upload, selectors, attached nuggets, etc.).
 */
export const StructuredQuestionInput = ({
  componentType,
  messageId,
  isTyping,
  onSelectOption,
}: StructuredQuestionInputProps) => {
  const skipId = `chat-question-skip-button-${messageId}`;
  const handleSkip = () => onSelectOption('Skipped', { LLMInput: '<SKIP>' });

  if (componentType.type === 'SINGLE_SELECT') {
    const promptOptions: PromptOption[] = componentType.options.map((option, i) => ({
      id: String(i),
      title: option,
    }));

    const buttons = (
      <PromptButtons
        onClick={(option) => onSelectOption(option.title)}
        isTyping={isTyping}
        promptOptions={promptOptions}
        variant="SINGLE_SELECT"
      />
    );

    if (componentType.required ?? true) {
      return buttons;
    }

    return withSkipFooter(buttons, skipId, isTyping, handleSkip);
  }

  if (componentType.type === 'MULTI_SELECT') {
    return (
      <MultiSelectChatQuestion
        idPrefix={`multi-select-chat-question-${messageId}`}
        options={componentType.options}
        exclusiveOptions={componentType.exclusiveOptions}
        required={componentType.required ?? true}
        isTyping={isTyping}
        onSubmit={(selected) => onSelectOption(selected.join(', '))}
        onSkip={handleSkip}
      />
    );
  }

  if (componentType.type === 'MULTI_NUMERIC') {
    return (
      <MultiNumericChatQuestion
        idPrefix={`multi-numeric-chat-question-${messageId}`}
        options={componentType.options}
        required={componentType.required ?? false}
        isTyping={isTyping}
        onSubmit={(formatted) => onSelectOption(formatted)}
        onSkip={handleSkip}
      />
    );
  }

  if (componentType.type === 'SKIP_ONLY') {
    return (
      <Box sx={chatSkipRowSx}>
        <SkipChatButton id={skipId} disabled={isTyping} onSkip={handleSkip} />
      </Box>
    );
  }

  if (componentType.type === 'NUMERIC') {
    // The numeric value itself is typed into the main chat input; this slot only
    // surfaces a skip button when the question is optional.
    if (componentType.required) return null;

    return (
      <Box sx={chatSkipRowSx}>
        <SkipChatButton id={skipId} disabled={isTyping} onSkip={handleSkip} />
      </Box>
    );
  }

  if (componentType.type === 'USER_SUGGESTION') {
    const list = (
      <UserSuggestionChatQuestion
        users={componentType.users}
        onSelect={(userId, name) => onSelectOption(name || userId, { LLMInput: userId })}
      />
    );

    if (componentType.required ?? false) {
      return list;
    }

    return withSkipFooter(list, skipId, isTyping, handleSkip);
  }

  return null;
};

const withSkipFooter = (
  content: ReactNode,
  skipId: string,
  isTyping: boolean,
  onSkip: () => void,
) => (
  <Box sx={chatQuestionWithSkipSx}>
    {content}
    <Box sx={chatSkipRowSx}>
      <SkipChatButton id={skipId} disabled={isTyping} onSkip={onSkip} />
    </Box>
  </Box>
);

const chatQuestionWithSkipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const chatSkipRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
};
